import React, { useState, useRef } from 'react'
import { LoadTester, loadTestConfigs, monitorPerformance, type LoadTestResult, type UserSession } from '../utils/loadTest'

const LoadTestPage: React.FC = () => {
  const [testRunning, setTestRunning] = useState(false)
  const [testResults, setTestResults] = useState<LoadTestResult | null>(null)
  const [sessionDetails, setSessionDetails] = useState<UserSession[]>([])
  const [selectedConfig, setSelectedConfig] = useState<keyof typeof loadTestConfigs>('light')
  const [customConfig, setCustomConfig] = useState({
    concurrentUsers: 10,
    testDurationMs: 30000,
    enableAnalytics: true
  })
  const [useCustomConfig, setUseCustomConfig] = useState(false)
  const [performanceData, setPerformanceData] = useState<any>(null)
  const [testLog, setTestLog] = useState<string[]>([])
  
  const loadTesterRef = useRef<LoadTester | null>(null)

  const addToLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    setTestLog(prev => [...prev.slice(-19), `[${timestamp}] ${message}`])
  }

  const runLoadTest = async () => {
    if (testRunning) return

    setTestRunning(true)
    setTestResults(null)
    setSessionDetails([])
    setTestLog([])
    
    addToLog('Initializing load test...')

    try {
      const config = useCustomConfig ? {
        ...customConfig,
        actionIntervalMs: 1000,
        targetEndpoint: window.location.origin
      } : loadTestConfigs[selectedConfig]

      addToLog(`Starting ${useCustomConfig ? 'custom' : selectedConfig} load test`)
      addToLog(`Configuration: ${config.concurrentUsers} users, ${config.testDurationMs}ms duration`)

      loadTesterRef.current = new LoadTester(config)
      
      // Monitor performance before test
      const initialPerf = monitorPerformance()
      setPerformanceData({ initial: initialPerf })
      addToLog(`Initial memory usage: ${initialPerf.memory?.used || 'N/A'}MB`)

      // Run the test
      const results = await loadTesterRef.current.runLoadTest()
      setTestResults(results)
      
      // Get session details
      const sessions = loadTesterRef.current.getSessionDetails()
      setSessionDetails(sessions)
      
      // Monitor performance after test
      const finalPerf = monitorPerformance()
      setPerformanceData((prev: any) => ({ ...prev, final: finalPerf }))
      
      addToLog(`Test completed successfully`)
      addToLog(`Results: ${results.successfulRequests}/${results.totalRequests} requests successful`)
      addToLog(`Error rate: ${results.errorRate.toFixed(2)}%`)
      addToLog(`Average response time: ${results.averageResponseTime.toFixed(0)}ms`)
      addToLog(`Final memory usage: ${finalPerf.memory?.used || 'N/A'}MB`)

    } catch (error) {
      addToLog(`Test failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
      console.error('Load test failed:', error)
    } finally {
      setTestRunning(false)
    }
  }

  const stopLoadTest = () => {
    if (loadTesterRef.current) {
      addToLog('Stopping load test...')
      // Note: The current implementation doesn't support stopping mid-test
      // This would require additional implementation
    }
  }

  const downloadReport = () => {
    if (!loadTesterRef.current) return

    const report = loadTesterRef.current.generateReport()
    const blob = new Blob([report], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `load-test-report-${new Date().toISOString().slice(0, 19)}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`
  }

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: 'white', 
      color: 'black',
      minHeight: '100vh',
      fontFamily: 'monospace'
    }}>
      <h1>Load Testing Dashboard</h1>
      <p>Test the system's performance under load</p>

      {/* Configuration Section */}
      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
        <h2>Test Configuration</h2>
        
        <div style={{ marginBottom: '20px' }}>
          <label>
            <input
              type="radio"
              value="preset"
              checked={!useCustomConfig}
              onChange={() => setUseCustomConfig(false)}
            />
            Preset Configuration
          </label>
          
          {!useCustomConfig && (
            <div style={{ marginLeft: '20px', marginTop: '10px' }}>
              <select 
                value={selectedConfig} 
                onChange={(e) => setSelectedConfig(e.target.value as keyof typeof loadTestConfigs)}
                style={{ padding: '5px', marginRight: '10px' }}
              >
                <option value="light">Light (10 users, 30s)</option>
                <option value="medium">Medium (50 users, 2m)</option>
                <option value="heavy">Heavy (100 users, 5m)</option>
                <option value="stress">Stress (200 users, 10m)</option>
              </select>
              
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                {selectedConfig && (
                  <div>
                    <div>Concurrent Users: {loadTestConfigs[selectedConfig].concurrentUsers}</div>
                    <div>Duration: {formatDuration(loadTestConfigs[selectedConfig].testDurationMs)}</div>
                    <div>Analytics: {loadTestConfigs[selectedConfig].enableAnalytics ? 'Enabled' : 'Disabled'}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label>
            <input
              type="radio"
              value="custom"
              checked={useCustomConfig}
              onChange={() => setUseCustomConfig(true)}
            />
            Custom Configuration
          </label>
          
          {useCustomConfig && (
            <div style={{ marginLeft: '20px', marginTop: '10px' }}>
              <div style={{ marginBottom: '10px' }}>
                <label>Concurrent Users: </label>
                <input
                  type="number"
                  value={customConfig.concurrentUsers}
                  onChange={(e) => setCustomConfig(prev => ({ ...prev, concurrentUsers: parseInt(e.target.value) || 1 }))}
                  min="1"
                  max="500"
                  style={{ padding: '5px', width: '80px' }}
                />
              </div>
              
              <div style={{ marginBottom: '10px' }}>
                <label>Duration (seconds): </label>
                <input
                  type="number"
                  value={customConfig.testDurationMs / 1000}
                  onChange={(e) => setCustomConfig(prev => ({ ...prev, testDurationMs: (parseInt(e.target.value) || 1) * 1000 }))}
                  min="1"
                  max="1800"
                  style={{ padding: '5px', width: '80px' }}
                />
              </div>
              
              <div>
                <label>
                  <input
                    type="checkbox"
                    checked={customConfig.enableAnalytics}
                    onChange={(e) => setCustomConfig(prev => ({ ...prev, enableAnalytics: e.target.checked }))}
                  />
                  Enable Analytics Tracking
                </label>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={runLoadTest}
            disabled={testRunning}
            style={{
              padding: '10px 20px',
              backgroundColor: testRunning ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: testRunning ? 'not-allowed' : 'pointer'
            }}
          >
            {testRunning ? 'Running Test...' : 'Start Load Test'}
          </button>
          
          {testRunning && (
            <button
              onClick={stopLoadTest}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Stop Test
            </button>
          )}
          
          {testResults && (
            <button
              onClick={downloadReport}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Download Report
            </button>
          )}
        </div>
      </div>

      {/* Live Log */}
      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
        <h3>Test Log</h3>
        <div style={{ 
          height: '200px', 
          overflowY: 'auto', 
          backgroundColor: '#1e1e1e', 
          color: '#00ff00', 
          padding: '10px', 
          fontFamily: 'Courier New, monospace',
          fontSize: '12px',
          border: '1px solid #333'
        }}>
          {testLog.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
          {testLog.length === 0 && <div style={{ color: '#666' }}>No logs yet...</div>}
        </div>
      </div>

      {/* Results Section */}
      {testResults && (
        <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
          <h2>Test Results</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginTop: '20px' }}>
            <div style={{ padding: '15px', backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <h4>Request Statistics</h4>
              <div>Total Requests: {testResults.totalRequests}</div>
              <div>Successful: {testResults.successfulRequests}</div>
              <div>Failed: {testResults.failedRequests}</div>
              <div>Success Rate: {((testResults.successfulRequests / testResults.totalRequests) * 100).toFixed(2)}%</div>
            </div>
            
            <div style={{ padding: '15px', backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <h4>Performance Metrics</h4>
              <div>Avg Response: {testResults.averageResponseTime.toFixed(0)}ms</div>
              <div>Min Response: {testResults.minResponseTime.toFixed(0)}ms</div>
              <div>Max Response: {testResults.maxResponseTime.toFixed(0)}ms</div>
              <div>Requests/sec: {testResults.requestsPerSecond.toFixed(2)}</div>
            </div>
            
            <div style={{ padding: '15px', backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <h4>Session Summary</h4>
              <div>Total Sessions: {sessionDetails.length}</div>
              <div>Completed: {sessionDetails.filter(s => s.currentState === 'completed').length}</div>
              <div>Errors: {sessionDetails.filter(s => s.currentState === 'error').length}</div>
              <div>Completion Rate: {sessionDetails.length > 0 ? ((sessionDetails.filter(s => s.currentState === 'completed').length / sessionDetails.length) * 100).toFixed(1) : 0}%</div>
            </div>
          </div>

          {testResults.errors.length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <h4>Recent Errors</h4>
              <div style={{ maxHeight: '150px', overflowY: 'auto', backgroundColor: 'white', padding: '10px', border: '1px solid #dee2e6' }}>
                {testResults.errors.slice(-10).map((error, index) => (
                  <div key={index} style={{ fontSize: '12px', color: '#dc3545', marginBottom: '5px' }}>
                    [{new Date(error.timestamp).toLocaleTimeString()}] {error.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance Monitoring */}
      {performanceData && (
        <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' }}>
          <h3>Performance Monitoring</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <h4>Memory Usage</h4>
              {performanceData.initial?.memory && (
                <div>
                  <div>Initial: {performanceData.initial.memory.used}MB / {performanceData.initial.memory.limit}MB</div>
                  {performanceData.final?.memory && (
                    <div>Final: {performanceData.final.memory.used}MB / {performanceData.final.memory.limit}MB</div>
                  )}
                </div>
              )}
            </div>
            
            <div>
              <h4>Network Conditions</h4>
              {performanceData.initial?.network && (
                <div>
                  <div>Connection: {performanceData.initial.network.effectiveType}</div>
                  <div>Downlink: {performanceData.initial.network.downlink}Mbps</div>
                  <div>RTT: {performanceData.initial.network.rtt}ms</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div style={{ marginTop: '40px', padding: '20px', backgroundColor: '#e9ecef', border: '1px solid #dee2e6', borderRadius: '4px' }}>
        <h3>Navigation</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <a href="/" style={{ padding: '10px', backgroundColor: '#007bff', color: 'white', textDecoration: 'none', borderRadius: '4px' }}>
            Home
          </a>
          <a href="/admin" style={{ padding: '10px', backgroundColor: '#dc3545', color: 'white', textDecoration: 'none', borderRadius: '4px' }}>
            Admin Dashboard
          </a>
          <a href="/test" style={{ padding: '10px', backgroundColor: '#28a745', color: 'white', textDecoration: 'none', borderRadius: '4px' }}>
            Test Page
          </a>
        </div>
      </div>
    </div>
  )
}

export default LoadTestPage